import React, { useState } from "react";
import { DialogContent, DialogTitle } from "~/components/dialog";
import { useSnackBar } from "~/components/snackbar";
import { ErrorRenderer, MutationForm } from "~/components/forms/MutationForm";
import FormBottomRow from "~/components/form-bottom-row";
import { Employee, ValidationResponse } from "~/gql/types";
import { Body1 } from "@material/react-typography";
import MaterialIcon from "@material/react-material-icon";
import { useUser } from "~/Auth/useUser";
import { formatFirstMiddleLast } from "~/utils/formatEmployeeName";
import {
  EmployeePropertyDialog,
  EmployeePropertyDialogButton,
} from "./EmployeePropertyDialog";
import { useUpdateDefaultDriveMutation } from "./updateDefaultDrive.generated";
import { CommandAuthorization, SharedDrive } from "~/gql/types2";
import { SharedDrivePickerFormField } from "~/visuals/organisms/SharedDrivePickerFormField";
import { useGetSharedDrivesQuery } from "~/refdata2/sharedDrives/query.hotchoc.generated";

export type DefaultDriveLetterProps = {
  employee: Employee;
  reset: () => void;
};

export const DefaultDriveLetter: React.FC<DefaultDriveLetterProps> = ({
  employee,
  reset,
}) => {
  const [open, setOpen] = useState(false);
  const alert = useSnackBar();
  const { commands } = useUser();

  const { data, loading: drivesLoading } = useGetSharedDrivesQuery();

  const sharedDrives = data?.projectFolders?.drives as SharedDrive[];

  const employeeName = formatFirstMiddleLast(employee);

  const currentDriveLetter = employee.defaultDrive ?? null;

  const onSuccess = () => {
    alert({
      message: `${employeeName} default drive updated`,
      isSuccess: true,
      key: `${Math.random()}`,
    });
    reset();
    setOpen(false);
  };

  const [doMutation, { loading: mutationLoading }] =
    useUpdateDefaultDriveMutation();

  const runMutation = async (values: { driveLetter: string | null }) => {
    const variables = {
      userPrincipalName: employee.userPrincipalName,
      driveLetter: values?.driveLetter ?? null,
    };

    const result = await doMutation({ variables });

    return result?.data?.employees?.updateDefaultDrive as ValidationResponse;
  };

  const isAuthorized = commands.has(
    CommandAuthorization.UpdateDefaultDriveCommand
  );

  const onClick = () => {
    if (isAuthorized) {
      setOpen(true);
    }
  };

  return (
    <>
      <EmployeePropertyDialogButton role="button" onClick={onClick}>
        <Body1 title="Default Folder Drive Letter">
          <MaterialIcon icon={"folder_open"} />
          Default Drive: {currentDriveLetter ?? "(none)"}
        </Body1>
      </EmployeePropertyDialogButton>
      <EmployeePropertyDialog
        open={open}
        setOpen={setOpen}
        loading={mutationLoading}
      >
        <DialogTitle>{`${employeeName} Default Drive`}</DialogTitle>
        <DialogContent>
          {open && (
            <>
              <Body1>
                The drive letter selected will be used to populate the drive
                letter field in project requests for the respective project
                manager.
              </Body1>
              <MutationForm
                initialValues={{ driveLetter: currentDriveLetter ?? null }}
                runMutation={runMutation}
                allowPristineSubmit
                onSuccess={onSuccess}
              >
                <ErrorRenderer
                  render={(error) => (
                    <>
                      <SharedDrivePickerFormField
                        formField="driveLetter"
                        label="Default Drive"
                        sharedDriveOptions={sharedDrives}
                        loading={drivesLoading}
                      />
                      <FormBottomRow
                        errorMessage={error}
                        buttonText="Save"
                        onCancel={() => setOpen(false)}
                      />
                    </>
                  )}
                />
              </MutationForm>
            </>
          )}
        </DialogContent>
      </EmployeePropertyDialog>
    </>
  );
};
